import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { NoPermission } from '../design-system/NoPermission';
import apiClient from '../utils/apiClient';

export default function AdminRoute({ children, token, authLoading }) {
    const { decodedUser } = useAuth();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [hasPermission, setHasPermission] = useState(false);

    const checkPermission = useCallback(async () => {
        if (!token) {
            setHasPermission(false);
            setLoading(false);
            return;
        }

        const type = decodedUser?.type || '';
        if (type === 'admin' || type === 'super_admin') {
            setHasPermission(true);
            setLoading(false);
            return;
        }

        try {
            const res = await apiClient.get('/admin/my-permissions', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const perms = res?.data?.data?.permissions || [];
            setHasPermission(perms.some((p) => p === 'panel:admin' || p === '*:*'));
        } catch {
            setHasPermission(false);
        } finally {
            setLoading(false);
        }
    }, [token, decodedUser]);

    useEffect(() => {
        if (!authLoading) checkPermission();
    }, [checkPermission, authLoading]);

    if (authLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
            </div>
        );
    }

    if (!token) {
        return <Navigate to="/login?type=admin" state={{ from: location }} replace />;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] text-gray-400 text-sm">
                Checking permissions...
            </div>
        );
    }

    if (!hasPermission) {
        return (
            <NoPermission
                role="admin"
                message="Only system administrators can access this panel."
                actionLabel="Back to Home"
                actionTo="/"
            />
        );
    }

    return children;
}
